import { useState } from "react";
import { Link } from "react-router-dom";
import {
  WashingMachine, Refrigerator, Microwave, Wind, Wrench,
  ShieldCheck, Clock3, Star, ArrowRight, Phone, MapPin, Rocket, Award, CircuitBoard,
  Menu, X,
} from "lucide-react";
import BookingWizard from "@/components/BookingWizard";
import Testimonials from "@/components/Testimonials";
import { REVIEWS } from "@/data/reviews";

const APPLIANCES = [
  { key: "refrigerator", label: "Refrigerator", Icon: Refrigerator, blurb: "Compressor · ice maker · sensor boards" },
  { key: "washer", label: "Washer", Icon: WashingMachine, blurb: "Top & front-load · all brands" },
  { key: "dryer", label: "Dryer", Icon: Wind, blurb: "Gas · electric · vent diagnostics" },
  { key: "oven", label: "Oven & Range", Icon: Microwave, blurb: "Igniter · element · control board" },
  { key: "dishwasher", label: "Dishwasher", Icon: Wrench, blurb: "Pump · valve · spray arm" },
  { key: "microwave", label: "Built-in Microwave", Icon: Microwave, blurb: "Magnetron · door switch" },
  { key: "disposal", label: "Disposal", Icon: Wrench, blurb: "Reset · replace · jam" },
  { key: "ice-maker", label: "Ice Maker", Icon: Wrench, blurb: "Valve · mold · sensor" },
];

const BRANDS = ["Samsung", "LG", "Whirlpool", "GE", "KitchenAid", "Maytag", "Bosch", "Frigidaire", "Sub-Zero", "Wolf", "Viking", "Kenmore"];

const NEIGHBORHOODS = [
  "Alamo Heights", "Stone Oak", "Helotes", "Boerne", "Schertz",
  "Converse", "Live Oak", "Leon Valley", "Universal City", "Terrell Hills",
  "Olmos Park", "Castle Hills", "Shavano Park", "Hollywood Park", "Cibolo",
];

export default function Marketing() {
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <div className="retro min-h-screen relative overflow-hidden">
      {/* Background star field */}
      <div className="starfield fixed inset-0 opacity-80 pointer-events-none" />
      <div className="scanlines fixed inset-0 opacity-50 pointer-events-none" />

      {/* Top Alert Bar */}
      <div className="relative z-40 bg-orange text-white text-center text-xs sm:text-sm label-retro tracking-widest uppercase py-2.5 px-4" data-testid="grand-opening-alert">
        <span aria-hidden="true" className="mr-2">🚀</span>
        Mission Update: <span className="font-bold text-gold">Grand Opening Special</span> · Save <span className="font-bold">$10</span> on any repair today
      </div>

      {/* Top bar */}
      <header className="relative z-30 border-b border-cream/10 bg-navy/85 backdrop-blur-md sticky top-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-5 sm:px-8 h-16">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-home-link">
            <span className="h-10 w-10 rounded-full bg-[#F4EAD5] grid place-items-center overflow-hidden shrink-0">
              <img src="/brand/logo.png" alt="Space Appliance Repair" className="w-[94%] h-[94%] object-contain" style={{ mixBlendMode: "multiply" }} />
            </span>
            <div className="leading-tight">
              <div className="display-retro text-base text-cream uppercase">Space Appliance</div>
              <div className="label-retro text-[10px] text-orange tracking-[0.3em]">Repair Service</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm label-retro text-cream/70 uppercase">
            <a href="#services" className="hover:text-orange-2 transition-colors" data-testid="nav-services">Services</a>
            <a href="#reviews" className="hover:text-orange-2 transition-colors" data-testid="nav-reviews">Reviews</a>
            <a href="#team" className="hover:text-orange-2 transition-colors" data-testid="nav-team">Team</a>
            <a href="#brands" className="hover:text-orange-2 transition-colors" data-testid="nav-brands">Brands</a>
            <a href="#locations" className="hover:text-orange-2 transition-colors" data-testid="nav-locations">Locations</a>
            <Link to="/track" className="hover:text-orange-2 transition-colors" data-testid="nav-track">Track</Link>
          </nav>
          <div className="flex items-center gap-2">
            <a href="#book" data-testid="header-book-btn" className="hidden sm:inline-block">
              <button className="btn-orange">BOOK REPAIR</button>
            </a>
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-md border border-cream/20 text-cream hover:border-orange transition-colors"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="md:hidden border-t border-cream/10 bg-navy/95 backdrop-blur-md fade-up" data-testid="mobile-menu-panel">
            <nav className="max-w-7xl mx-auto px-5 py-5 grid gap-1 label-retro text-cream uppercase text-sm">
              {[
                { href: "#services", label: "Services", testid: "m-nav-services" },
                { href: "#book", label: "Book now", testid: "m-nav-book" },
                { href: "#reviews", label: "Reviews", testid: "m-nav-reviews" },
                { href: "#team", label: "Meet the team", testid: "m-nav-team" },
                { href: "#brands", label: "Brands", testid: "m-nav-brands" },
                { href: "#locations", label: "Locations", testid: "m-nav-locations" },
              ].map((it) => (
                <a key={it.href} href={it.href} onClick={() => setMobileOpen(false)} data-testid={it.testid} className="px-3 py-3 rounded-md hover:bg-cream/5 hover:text-orange-2 transition-colors">
                  {it.label}
                </a>
              ))}
              <Link to="/track" onClick={() => setMobileOpen(false)} data-testid="m-nav-track" className="px-3 py-3 rounded-md hover:bg-cream/5 hover:text-orange-2 transition-colors">
                Track booking
              </Link>
              <a href="tel:+12108001710" data-testid="m-nav-call" className="mt-2 inline-flex items-center justify-center gap-2 btn-orange text-sm">
                <Phone className="w-4 h-4" /> Call (210) 800-1710
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-20 sm:pt-24 sm:pb-28 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7 fade-up">
          <div className="chip"><Rocket className="w-3.5 h-3.5" /> Mission control · San Antonio</div>
          <div className="label-retro text-xs uppercase tracking-widest text-orange mt-6">Introducing</div>
          <h1 className="display-retro mt-3 text-cream text-[40px] sm:text-[58px] lg:text-[68px] leading-[1.05] normal-case font-semibold">
            Space Appliance Repair<br/>
            <span className="text-orange">Out-of-this-world <span className="text-gold">service.</span></span>
          </h1>
          <p className="mt-6 text-cream/80 text-lg max-w-2xl leading-relaxed">
            Appliance orbit failing? Our certified <span className="text-gold font-medium">tech-monauts</span> provide rapid,
            same-day repairs to bring your kitchen and laundry back to earth.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <a href="#book" data-testid="hero-book-btn">
              <button className="btn-orange flex items-center gap-2 label-retro text-sm">
                REQUEST MISSION CONTROL NOW <ArrowRight className="w-4 h-4" />
              </button>
            </a>
            <a href="tel:+12108001710" data-testid="hero-call-btn">
              <button className="btn-ghost flex items-center gap-2 label-retro text-sm">
                <Phone className="w-4 h-4" /> (210) 800-1710
              </button>
            </a>
          </div>
          <div className="mt-12 flex flex-wrap gap-x-6 gap-y-3 text-xs label-retro text-cream/70">
            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-gold" /> Licensed & insured</div>
            <div className="flex items-center gap-2"><Clock3 className="w-4 h-4 text-gold" /> Same-day windows</div>
            <div className="flex items-center gap-2"><Star className="w-4 h-4 text-gold" /> 5.0★ · 111 Google reviews</div>
            <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gold" /> 5504 Bandera Rd · San Antonio TX 78238</div>
          </div>
        </div>

        {/* Logo + atomic rings showcase */}
        <div className="md:col-span-5 fade-up">
          <div className="relative aspect-square max-w-[460px] mx-auto">
            <div className="atomic-ring absolute inset-0" />
            <div className="absolute inset-[10%] rounded-full bg-[#F4EAD5] overflow-hidden grid place-items-center shadow-[0_0_60px_rgba(230,92,0,0.25)] border-4 border-orange/70">
              <img src="/brand/logo.png" alt="Space Appliance Repair planet logo" className="w-[94%] h-[94%] object-contain" style={{ mixBlendMode: "multiply" }} />
            </div>
            {/* Outer accent ring */}
            <div className="absolute inset-0 rounded-full border border-cream/15 pointer-events-none" />
            {/* Orbiting dot */}
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-orange shadow-[0_0_20px_rgba(230,92,0,0.8)]" />
            <div className="absolute bottom-6 right-6 w-2 h-2 rounded-full bg-gold/80 shadow-[0_0_12px_rgba(245,197,66,0.8)]" />
          </div>
        </div>
      </section>

      {/* TRUST STRIPE */}
      <section className="relative z-10 border-y border-cream/10 bg-navy-2/60">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          <Stat n="2,400+" l="jobs / yr" />
          <Stat n="12 min" l="avg diagnosis" />
          <Stat n="92%" l="first-visit fix" />
          <Stat n="5.0★" l="111 Google reviews" />
        </div>
      </section>

      {/* BOOK NOW (embedded wizard) */}
      <section id="book" className="relative z-10 py-20 sm:py-24 scroll-mt-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="text-center">
            <div className="chip"><Rocket className="w-3.5 h-3.5" /> Mission Control · Booking</div>
            <h2 className="display-retro mt-4 text-cream text-3xl sm:text-4xl uppercase">Book appointment</h2>
            <p className="mt-3 text-cream/70 max-w-xl mx-auto">
              60 seconds. A dispatcher in San Antonio confirms your window in under 30 minutes.
              Use code <span className="text-gold font-semibold tracking-widest">GRAND_OPENING</span> for <span className="text-gold font-semibold">$10 off</span> your first repair.
            </p>
          </div>
          <div className="mt-10">
            <BookingWizard variant="embed" />
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="relative z-10 py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="chip mb-3"><CircuitBoard className="w-3.5 h-3.5" /> Module 01</div>
              <h2 className="display-retro text-cream text-3xl sm:text-4xl uppercase">What we fix</h2>
              <p className="text-cream/60 mt-2 max-w-lg">Every major home appliance, in your home, in San Antonio — usually same day.</p>
            </div>
            <a href="#book" className="text-orange hover:text-orange-2 label-retro text-xs uppercase" data-testid="services-book-link">
              Request mission control →
            </a>
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {APPLIANCES.map(({ key, label, Icon, blurb }) => (
              <Link
                key={key}
                to={`/services/${key}`}
                data-testid={`service-card-${key}`}
                id={`service-${key}`}
                className="group bg-navy-2/70 border border-cream/10 hover:border-orange/60 rounded-md p-5 transition-all duration-200 hover:-translate-y-1"
              >
                <div className="w-11 h-11 rounded-md bg-orange/10 text-orange grid place-items-center group-hover:bg-orange group-hover:text-white transition-colors border border-orange/40">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="display-retro mt-4 text-cream text-base uppercase tracking-wider">{label}</div>
                <div className="text-xs text-cream/60 mt-1 leading-relaxed">{blurb}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOW */}
      <section id="how" className="relative z-10 py-20 sm:py-24 border-y border-cream/10 bg-navy-2/40">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="text-center">
            <div className="chip"><Award className="w-3.5 h-3.5" /> Module 02</div>
            <h2 className="display-retro mt-4 text-cream text-3xl sm:text-4xl uppercase">Three honest steps</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {[
              { n: "01", h: "Send signal", p: "60 seconds. Phone, address, appliance, the symptom. Mission control receives in real time." },
              { n: "02", h: "Pick your window", p: "Same-day or next-morning. You get the name of your pilot and exactly when they arrive." },
              { n: "03", h: "Approve the quote", p: "Flat-rate diagnostic. We write the price before any work begins. No upsells. No nonsense." },
            ].map((s) => (
              <div key={s.n} className="relative bg-navy/60 border border-cream/15 rounded-md p-7">
                <div className="display-retro text-orange text-3xl">{s.n}</div>
                <div className="display-retro mt-3 text-cream text-xl uppercase">{s.h}</div>
                <div className="text-sm text-cream/70 mt-3 leading-relaxed">{s.p}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BRANDS */}
      <section id="brands" className="relative z-10 py-20 sm:py-24">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="text-center">
            <div className="chip">Module 03</div>
            <h2 className="display-retro mt-4 text-cream text-3xl sm:text-4xl uppercase">Certified on every brand</h2>
            <p className="text-cream/60 mt-3 max-w-xl mx-auto">Factory-trained on the names you have at home.</p>
          </div>
          <ul className="mt-12 flex flex-wrap items-center justify-center gap-3">
            {BRANDS.map((b) => (
              <li key={b} id={`brand-${b.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
                <Link to={`/brands/${b.toLowerCase().replace(/[^a-z0-9]/g, "-")}`} data-testid={`brand-${b.toLowerCase()}`}
                  className="label-retro text-xs uppercase tracking-widest text-cream/80 border border-cream/20 hover:border-orange hover:text-orange transition-colors rounded-full px-5 py-2.5 inline-block">
                  {b}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials reviews={REVIEWS.slice(0, 6)} itemName="Appliance Repair · San Antonio" id="ld-reviews-home" />

      {/* MEET TECH RAY */}
      <section id="team" className="relative z-10 py-20 sm:py-24 border-y border-cream/10 bg-navy-2/30">        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 fade-up">
            <div className="relative aspect-[4/5] max-w-md mx-auto rounded-xl overflow-hidden border-2 border-orange/40 shadow-[0_0_60px_rgba(230,92,0,0.2)]">
              <img
                src="/brand/tech-ray.jpg"
                alt="Senior Tech Ray of Space Appliance Repair standing by the company van"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute bottom-0 inset-x-0">
                <div className="h-16 bg-gradient-to-t from-navy to-transparent" />
                <div className="bg-navy px-5 pt-2 pb-4 border-t border-orange/30">
                  <div className="label-retro text-[11px] uppercase tracking-widest text-orange font-semibold">San Antonio · Senior Tech</div>
                  <div className="display-retro text-cream text-2xl sm:text-3xl mt-0.5">Ray</div>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 fade-up">
            <div className="chip"><Award className="w-3.5 h-3.5" /> Meet the crew</div>
            <h2 className="display-retro mt-5 text-cream text-3xl sm:text-5xl normal-case font-semibold leading-[1.05]">
              The tech-monaut on <span className="text-orange">your call</span><span className="text-gold">.</span>
            </h2>
            <p className="mt-5 text-cream/80 text-lg leading-relaxed max-w-xl">
              Ray heads our San Antonio crew. Factory-trained on every brand we service — and yes, that includes the temperamental
              Sub-Zero compressors and Wolf dual-stack burners. <span className="text-gold font-medium">12-minute average diagnosis.</span>{" "}
              Real names. Real trucks. Real accountability.
            </p>
            <div className="mt-8 grid sm:grid-cols-3 gap-3">
              <Mini label="Years on tools" value="14+" />
              <Mini label="Brands certified" value="14" />
              <Mini label="Google reviews" value="111 · 5.0★" />
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#book"><button className="btn-orange label-retro text-sm" data-testid="team-book-btn">Request Ray's crew</button></a>
              <a href="tel:+12108001710"><button className="btn-ghost label-retro text-sm">(210) 800-1710</button></a>
            </div>
          </div>
        </div>
      </section>

      {/* LOCATIONS */}
      <section id="locations" className="relative z-10 py-20 sm:py-24 border-y border-cream/10 bg-navy-2/40">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="text-center">
            <div className="chip"><MapPin className="w-3.5 h-3.5" /> Module 04</div>
            <h2 className="display-retro mt-4 text-cream text-3xl sm:text-4xl uppercase">Service area</h2>
            <p className="text-cream/60 mt-3 max-w-xl mx-auto">Same-day windows across greater San Antonio and Bexar County.</p>
          </div>
          <ul className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {NEIGHBORHOODS.map((n) => (
              <li key={n} id={`location-${n.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
                <Link to={`/locations/${n.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                  data-testid={`location-${n.toLowerCase().replace(/\s+/g, "-")}`}
                  className="bg-navy/60 border border-cream/15 rounded-md px-4 py-3 text-sm text-cream/80 hover:border-orange hover:text-orange transition-colors flex items-center gap-2">
                  <MapPin className="w-3 h-3 text-orange" /> {n}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* POPULAR SEARCHES (combo links) */}
      <section id="popular" className="relative z-10 py-20 sm:py-24">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="text-center">
            <div className="chip"><CircuitBoard className="w-3.5 h-3.5" /> Module 05</div>
            <h2 className="display-retro mt-4 text-cream text-3xl sm:text-4xl uppercase">Popular searches</h2>
            <p className="text-cream/60 mt-3 max-w-xl mx-auto">Direct links to our most-requested appliance + neighborhood combinations.</p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5 text-sm">
            {[
              ["refrigerator", "stone-oak"], ["refrigerator", "alamo-heights"], ["refrigerator", "helotes"],
              ["washer", "stone-oak"], ["washer", "schertz"], ["washer", "leon-valley"],
              ["dryer", "alamo-heights"], ["dryer", "boerne"], ["dryer", "converse"],
              ["oven", "stone-oak"], ["oven", "alamo-heights"], ["oven", "schertz"],
              ["dishwasher", "stone-oak"], ["dishwasher", "helotes"], ["dishwasher", "universal-city"],
              ["ice-maker", "stone-oak"], ["ice-maker", "boerne"], ["ice-maker", "leon-valley"],
            ].map(([a, n]) => {
              const app = APPLIANCES.find((x) => x.key === a);
              const hood = NEIGHBORHOODS.find((x) => x.toLowerCase().replace(/[^a-z0-9]/g, "-") === n);
              if (!app || !hood) return null;
              return (
                <Link key={`${a}-${n}`} to={`/${a}-repair-${n}`}
                  data-testid={`popular-${a}-${n}`}
                  className="bg-navy-2/60 border border-cream/15 hover:border-orange rounded-md px-4 py-3 text-cream/85 hover:text-orange transition-colors flex items-center justify-between gap-3">
                  <span><span className="display-retro text-cream">{app.label}</span> <span className="text-cream/50">in</span> <span className="display-retro text-orange">{hood}</span></span>
                  <ArrowRight className="w-3.5 h-3.5 text-orange shrink-0" />
                </Link>
              );
            })}
          </div>
          <p className="text-center mt-8">
            <Link
              to="/sitemap"
              data-testid="popular-all-link"
              className="inline-flex items-center gap-2 text-xs label-retro text-cream/60 hover:text-orange uppercase tracking-widest transition-colors"
            >
              + 78 more appliance × neighborhood combinations <ArrowRight className="w-3 h-3" />
            </Link>
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 py-20 sm:py-24">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <div className="chip mx-auto"><Rocket className="w-3.5 h-3.5" /> Ready for takeoff</div>
          <h2 className="display-retro mt-6 text-cream text-3xl sm:text-5xl uppercase leading-[1.05]">
            Appliance grounded?<br/>
            <span className="text-orange">Call mission control.</span>
          </h2>
          <p className="mt-5 text-cream/70 max-w-xl mx-auto">Same-day rescue in San Antonio. Book in under 60 seconds — first repair gets <span className="text-gold font-medium">$10 off</span>.</p>
          <a href="#book" data-testid="cta-book-btn">
            <button className="btn-orange mt-10 label-retro tracking-widest text-sm">REQUEST MISSION CONTROL →</button>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-cream/10 bg-navy py-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-wrap items-center justify-between gap-4 text-xs label-retro text-cream/50 uppercase">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-full bg-[#F4EAD5] grid place-items-center overflow-hidden opacity-90">
              <img src="/brand/logo.png" alt="" className="w-[94%] h-[94%] object-contain" style={{ mixBlendMode: "multiply" }} />
            </span>
            <span>© 2026 Space Appliance Repair · 5504 Bandera Rd, San Antonio, TX 78238</span>
          </div>
          <div className="flex gap-5">
            <Link to="/sitemap" className="hover:text-orange" data-testid="footer-sitemap">All pages</Link>
            <Link to="/track" className="hover:text-orange" data-testid="footer-track">Track booking</Link>
            <Link to="/crm" className="hover:text-orange" data-testid="footer-crm">Staff login</Link>
            <Link to="/dispatch" className="hover:text-orange" data-testid="footer-dispatch">Dispatch</Link>
            <Link to="/tech" className="hover:text-orange" data-testid="footer-tech">Tech portal</Link>
          </div>
        </div>
      </footer>

      {/* Sticky Mobile Call SPACE */}
      <a
        href="tel:+12108001710"
        data-testid="sticky-call-space"
        className="md:hidden fixed bottom-5 inset-x-5 z-40 bg-orange text-white rounded-full h-14 flex items-center justify-center gap-2 label-retro uppercase tracking-widest text-sm font-semibold active:scale-95 transition"
      >
        <Phone className="w-5 h-5" /> Call SPACE · (210) 800-1710
      </a>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="display-retro text-2xl sm:text-3xl text-orange">{n}</div>
      <div className="label-retro text-[10px] uppercase tracking-widest text-cream/60 mt-1">{l}</div>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="bg-navy/60 border border-cream/15 rounded-md px-4 py-3">
      <div className="text-[10px] uppercase tracking-widest text-cream/60 label-retro">{label}</div>
      <div className="display-retro text-cream text-xl mt-1">{value}</div>
    </div>
  );
}
